/**
 * BrandIntro engine — the "digital system construction" piece, drawn on one canvas.
 *
 * One growing node field rendered as a pure function of authored time: loose
 * items scatter across an empty field while a cursor places the first few by
 * hand, links snap in and creation outruns the cursor, hundreds more items and
 * radial fan bursts crowd the field into a tangle as the camera pulls back, the
 * tangle migrates into six left-aligned racks, the ordered system rests, and
 * finally the racks converge into the Veylo mark while the surplus flies off and
 * the wordmark wipes in beside it.
 *
 * Framework-free on purpose: the React component owns mounting and theming,
 * this owns the time axis and the pixels.
 */

export interface IBrandIntroTheme {
  /**
   * One colour for the whole piece — items, links, the mark and the wordmark
   * are all drawn in it, and only their opacity differs. Keeping it to a single
   * ink is what makes the field read as one system rather than a diagram.
   */
  ink: string;
}

export interface IBrandIntroOptions {
  /**
   * The brand lockup — mark and wordmark side by side, one flat colour. It is
   * read as a silhouette and split on the gap between the two so the letters
   * can be lifted out and wiped in on their own; the mark itself is drawn from
   * `MARK` below rather than from the raster, because the mosaic needs to know
   * the shapes, not just their coverage.
   */
  lockupSrc: string;
  theme: IBrandIntroTheme;
  /**
   * Seconds for one full pass. Defaults to the authored length; any other value
   * rescales every scene by the same factor, so the beats keep their relative
   * weight.
   */
  duration?: number;
  loop: boolean;
  /** Seconds the finished lockup is held before a loop restarts. */
  gap: number;
  /** Draw the finished lockup and nothing else. */
  reducedMotion: boolean;
  /**
   * The share of the canvas width the composition is laid out against, 0–1.
   * Below 1 the field runs past the composition and off the canvas rather than
   * stopping at its edge — for a full-bleed backdrop the page fades out itself.
   */
  stage?: number;
  /** Where the composition's centre sits across the canvas, 0–1. */
  focusX?: number;
  /**
   * The share of the canvas width that reaches the viewer unfaded, 0–1. A page
   * that dissolves the piece under its own content should say so here: the
   * field is happy to run off into the fade, but the cursor keeps to the clear
   * ground so it is never seen clicking on something that is not there.
   */
  clearWidth?: number;
}

export interface IBrandIntroHandle {
  /**
   * Seconds in one pass. Shorter than `BRAND_INTRO_DURATION` unless looping,
   * because a single pass stops on the finished lockup rather than playing the
   * closing fade that hides a loop's seam.
   */
  readonly duration: number;
  /** Recolour without rebuilding the field — used when the theme toggles. */
  setTheme: (theme: IBrandIntroTheme) => void;
  replay: () => void;
  /** Stop and hold one frame, `t` seconds into the piece. */
  seek: (t: number) => void;
  destroy: () => void;
}

/* ------------------------------------------------------------------ *
 * timeline
 *
 * Each scene is a named slice with two lengths: `dur` is how long it plays
 * for, `nat` is the slice of authored time it covers. The whole choreography
 * below is written against authored seconds; playback time is warped onto it
 * per scene, so a scene can run faster or slower than it was authored without
 * any of the beats moving.
 * ------------------------------------------------------------------ */

/**
 * The piece reads as three movements, and the `dur` column is budgeted against
 * them rather than against each scene on its own:
 *
 *   items appear and link   Scatter + Link                 1.6s
 *   the field gathers       Proliferate + Resolve + Settle 3.1s
 *   the mark signs          Signature                      1.5s
 *
 * Within a movement each scene is budgeted by how much of it there is to read
 * rather than by the authored time it covers, so `dur` and `nat` drift apart
 * freely: Scatter and Link split their 1.6s evenly though Link is authored the
 * longer of the two, and Settle is cut to a beat because a rest needs no time
 * to read. Moving budget between movements therefore re-weights them without
 * re-choreographing anything.
 *
 * Signature is the exception to reading its `dur` as screen time: a single pass
 * stops at `HOLD` (authored 26.0s), which is 8/11 of the way through the scene,
 * so its 2.0625s of playback is the 1.5s above. The remainder is the closing
 * fade, which only a loop ever plays.
 */
const SCENES = [
  { name: 'Scatter', dur: 0.9, nat: 4.5 },
  { name: 'Link', dur: 0.9, nat: 5.5 },
  { name: 'Proliferate', dur: 1.2, nat: 5 },
  { name: 'Resolve', dur: 1.4, nat: 5 },
  { name: 'Settle', dur: 0.5, nat: 2 },
  { name: 'Signature', dur: 2.0625, nat: 5.5 },
];

const TIMELINE = (() => {
  let playStart = 0;
  let authStart = 0;
  const sections = SCENES.map((s) => {
    const row = { playStart, dur: s.dur, authStart, nat: s.nat };
    playStart += s.dur;
    authStart += s.nat;
    return row;
  });
  return { sections, total: playStart, authoredTotal: authStart };
})();

/** One full pass, in seconds — 6.7625s, of which a non-looping pass plays 6.2s. */
export const BRAND_INTRO_DURATION = TIMELINE.total;

/** Playback seconds -> authored seconds. */
function warp(t: number): number {
  const ss = TIMELINE.sections;
  let idx = ss.length - 1;
  for (let i = 0; i < ss.length; i++) {
    if (t < ss[i].playStart + ss[i].dur) {
      idx = i;
      break;
    }
  }
  const s = ss[idx];
  const local = Math.min(Math.max(t - s.playStart, 0), s.dur);
  const T = s.authStart + (s.dur > 0 ? local * (s.nat / s.dur) : 0);
  return Math.min(T, TIMELINE.authoredTotal);
}

/** Authored seconds -> playback seconds. */
function unwarp(T: number): number {
  const ss = TIMELINE.sections;
  let idx = ss.length - 1;
  for (let i = 0; i < ss.length; i++) {
    if (T < ss[i].authStart + ss[i].nat) {
      idx = i;
      break;
    }
  }
  const s = ss[idx];
  const local = Math.min(Math.max(T - s.authStart, 0), s.nat);
  return s.playStart + (s.nat > 0 ? local * (s.dur / s.nat) : 0);
}

/* ------------------------------------------------------------------ *
 * authored constants
 * ------------------------------------------------------------------ */

/** Author space — the composition is a portrait frame. */
const W = 1080;
const H = 1440;

const POP = 0.5; // node birth duration
const DRAW = 0.52; // edge draw duration
const MOVE = 1.55; // scatter -> ordered migration duration
const TOTAL = TIMELINE.authoredTotal; // 27.5 authored seconds
/** The signature: convergence, the solid mark taking over, the wordmark wipe. */
const SIG = { t0: 22.0, move: 1.3, solid: 24.4, word: 24.95 };
/** Authored start of each scene — the camera and the field are keyed to these. */
const INT = [0, 4.5, 10, 15, 20, 22, TOTAL];

/**
 * The Veylo mark: five shapes on the source 1024x560 grid. These are the icon
 * half of `veylo-wordmark-*.svg` measured off its own geometry — the mosaic
 * tiles them, so it needs the shapes rather than a silhouette.
 */
const MARK = [
  { x: 0, y: 0, w: 448, h: 140, pill: 1 },
  { x: 884, y: 0, w: 140, h: 140, pill: 0 },
  { x: 167, y: 210, w: 365, h: 140, pill: 1 },
  { x: 706, y: 210, w: 141, h: 140, pill: 0 },
  { x: 391, y: 419, w: 281, h: 141, pill: 0 },
];

/**
 * Where the letters' baseline sits in their own box, as a fraction of its
 * height — the mark is set flush with that band rather than with the box, so
 * the descender does not push it off the line. Measured off the brand artwork
 * (baseline 331.6 of a 422.44-tall letter box); the raster is probed for it on
 * load and falls back to this.
 */
const WORD_CAP = 331.6 / 422.44;

/** The letters' aspect ratio, from the same artwork. Also probed on load. */
const WORD_AR = 1189.03 / 422.44;

/** The letters' share of the lockup's width — where they start, and how wide. */
const WORD_SHARE = { x: 730.97 / 1920, w: 1189.03 / 1920 };

/**
 * Authored second at which the lockup stands finished: the mark solid, the
 * wordmark fully wiped in, the camera already at rest and the closing fade not
 * yet started. Unless the piece is looping it stops here and holds this frame,
 * so the logo stays on screen.
 */
const HOLD = SIG.word + 1.05;

/** The same moment in playback seconds — 6.2s of the 6.7625s pass. */
const REST = unwarp(HOLD);

const COLS = 6;
const COL_PITCH = 232;
const ROW_GAP = 6;
const COL_X = (c: number) => -((COLS - 1) / 2) * COL_PITCH + c * COL_PITCH;
const LEFT_INSET = 46;

/* ------------------------------------------------------------------ *
 * math
 * ------------------------------------------------------------------ */

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const seg = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));

const MOTION = {
  enter: (t: number) => 1 - Math.pow(1 - t, 3),
  draw: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  /** Overshoots past 1 and settles — the pop of an item arriving. */
  pop: (t: number) => 1 + 2.05 * Math.pow(t - 1, 3) + 1.1 * Math.pow(t - 1, 2),
};

/** Piecewise-linear interpolation through a table of x/y pairs. */
function pw(xs: number[], ys: number[]) {
  return (x: number) => {
    const n = xs.length;
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 1;
    while (i < n - 1 && x > xs[i]) i++;
    const u = (x - xs[i - 1]) / (xs[i] - xs[i - 1]);
    return ys[i - 1] + (ys[i] - ys[i - 1]) * u;
  };
}

/** Deterministic PRNG, so the field is identical on every mount. */
function mulberry(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ------------------------------------------------------------------ *
 * the field
 * ------------------------------------------------------------------ */

interface INode {
  i: number;
  /** Rack column. */
  c: number;
  /** Index of the node this one hangs off, -1 for the first. */
  p: number;
  w: number;
  h: number;
  /** Scattered position. */
  sx: number;
  sy: number;
  /** Ordered position, in the racks. */
  ox: number;
  oy: number;
  chip: number;
  birth: number;
  dim: number;
  slot: number;
  fan: number;
  ord: number;
  /** Where in its column this node sits, 0–1. */
  rowFrac: number;
  /** Migration time — when it leaves the scatter for its rack. */
  rt: number;
}

interface IEdge {
  a: number;
  b: number;
  curv: number;
  born: number;
  /** Resting opacity. */
  o: number;
}

interface IGraph {
  nodes: INode[];
  edges: IEdge[];
  pulses: { e: IEdge; t0: number; dur: number; gap: number }[];
  blinks: { n: INode; t0: number; dur: number }[];
}

function buildGraph(): IGraph {
  const R = mulberry(0x5eed1a3);
  const nodes: INode[] = [];
  const edges: IEdge[] = [];

  const pickSize = (chip: boolean): [number, number] => {
    if (chip) return [7 + R() * 5, 6 + R() * 3];
    const k = R();
    if (k < 0.54) return [34 + R() * 56, 7 + R() * 4]; // label bars
    if (k < 0.84) return [22 + R() * 24, 14 + R() * 13]; // cards
    return [13 + R() * 12, 7 + R() * 6]; // chips
  };

  // Scatter placement with light rejection, so the field breathes.
  const placed: [number, number][] = [];
  const scatterAt = (cx: number, cy: number, rad: number, minD: number): [number, number] => {
    let bx = 0;
    let by = 0;
    let bs = -1;
    for (let a = 0; a < 9; a++) {
      const ang = R() * Math.PI * 2;
      const rr = Math.sqrt(R()) * rad;
      const x = cx + Math.cos(ang) * rr;
      const y = cy + Math.sin(ang) * rr * 1.24;
      let near = 1e9;
      for (let i = Math.max(0, placed.length - 90); i < placed.length; i++) {
        const d = Math.hypot(placed[i][0] - x, placed[i][1] - y);
        if (d < near) near = d;
      }
      if (near > bs) {
        bs = near;
        bx = x;
        by = y;
      }
      if (near > minD) break;
    }
    placed.push([bx, by]);
    return [bx, by];
  };

  const N = 372;
  const birthOf = pw(
    [0, 1, 4, 10, 20, 36, 84, 172, 290, N - 1],
    [0.35, 0.85, 1.65, 2.7, 3.75, 4.6, 8.6, 11.8, 14.6, 16.6],
  );

  const colCount = new Array(COLS).fill(0);
  const colH = new Array(COLS).fill(0);
  const leastCol = () => {
    let a = 0;
    let b = 1;
    for (let k = 1; k < COLS; k++) if (colH[k] < colH[a]) a = k;
    for (let k = 0; k < COLS; k++) if (k !== a && (b === a || colH[k] < colH[b])) b = k;
    return R() < 0.78 ? a : b;
  };
  for (let i = 0; i < N; i++) {
    // Column assignment keeps the six racks the same height.
    const c = i < COLS ? i : leastCol();
    // Parent: an earlier node, strongly preferring the same column.
    let p = -1;
    if (i > 0) {
      for (let a = 0; a < 26; a++) {
        const j = Math.floor(Math.pow(R(), 1.5) * i);
        if (nodes[j].c === c) {
          p = j;
          break;
        }
      }
      if (p < 0) p = Math.floor(Math.pow(R(), 1.5) * i);
    }
    const chip = i > 90 && R() < 0.26;
    const sz = pickSize(chip);
    colH[c] += sz[1] + ROW_GAP;
    let sx: number;
    let sy: number;
    if (i < 40) {
      const s = scatterAt(COL_X(c) * 0.5 + (R() - 0.5) * 180, (R() - 0.5) * 900, 210, 78);
      sx = s[0];
      sy = s[1];
    } else {
      const par = nodes[p];
      const near = R() < 0.62;
      if (near) {
        const s = scatterAt(par.sx, par.sy, 130 + R() * 90, 34);
        sx = s[0];
        sy = s[1];
      } else {
        const s = scatterAt(COL_X(c) * 0.85, (R() - 0.5) * 1500, 300, 34);
        sx = s[0];
        sy = s[1];
      }
    }
    sx = Math.max(-700, Math.min(700, sx));
    sy = Math.max(-900, Math.min(900, sy));
    nodes.push({
      i,
      c,
      p,
      w: sz[0],
      h: sz[1],
      sx,
      sy,
      chip: chip ? 1 : 0,
      birth: birthOf(i),
      dim: chip ? 0.55 + R() * 0.2 : 0.72 + R() * 0.28,
      slot: colCount[c]++,
      fan: 0,
      ord: R(),
      ox: 0,
      oy: 0,
      rowFrac: 0,
      rt: 0,
    });
  }

  // Fan bursts: a few hubs sprout a radial ring of chips as the system scales.
  const hubs: INode[] = [];
  const usedCol: Record<number, number> = {};
  for (let a = 0; a < 60 && hubs.length < 6; a++) {
    const h = nodes[60 + Math.floor(R() * 200)];
    if (h && !h.chip && !usedCol[h.c]) {
      usedCol[h.c] = 1;
      hubs.push(h);
    }
  }
  hubs.forEach((h, hi) => {
    const count = 9 + Math.floor(R() * 6);
    const rad = 52 + R() * 26;
    const t0 = 11.4 + hi * 0.62;
    for (let k = 0; k < count; k++) {
      const ang = (k / count) * Math.PI * 2 + R() * 0.16;
      const sz = pickSize(true);
      const c = h.c;
      nodes.push({
        i: nodes.length,
        c,
        p: h.i,
        w: sz[0],
        h: sz[1],
        sx: h.sx + Math.cos(ang) * rad,
        sy: h.sy + Math.sin(ang) * rad * 1.1,
        chip: 1,
        birth: t0 + (k / count) * 0.55 + R() * 0.08,
        dim: 0.5 + R() * 0.2,
        slot: colCount[c]++,
        fan: 1,
        ord: R(),
        ox: 0,
        oy: 0,
        rowFrac: 0,
        rt: 0,
      });
      colH[c] += sz[1] + ROW_GAP;
    }
  });

  // Ordered layout: six left-aligned racks.
  const cursorY = new Array(COLS).fill(0);
  const colTotal = new Array(COLS).fill(0);
  for (const n of nodes) colTotal[n.c] += n.h + ROW_GAP;
  for (let c = 0; c < COLS; c++) cursorY[c] = -colTotal[c] / 2;
  const order = nodes.slice().sort((a, b) => a.c - b.c || a.ord - b.ord);
  for (const n of order) {
    n.oy = cursorY[n.c] + n.h / 2;
    cursorY[n.c] += n.h + ROW_GAP;
    n.ox = COL_X(n.c) - LEFT_INSET + n.w / 2;
    n.rowFrac = 0;
  }
  for (const n of nodes) n.rowFrac = (n.oy + colTotal[n.c] / 2) / Math.max(1, colTotal[n.c]);

  // Migration timing — a cascade left to right, top to bottom.
  for (const n of nodes) {
    n.rt = Math.max(n.birth + 0.35, 15.25 + n.c * 0.42 + n.rowFrac * 0.85);
    n.rt += (n.i % 7) * 0.035;
    n.rt = Math.min(n.rt, 19.3);
  }

  // Edges.
  const addEdge = (a: number, b: number, extra: number) => {
    const k = (R() - 0.5) * (extra ? 0.5 : 0.3);
    edges.push({
      a,
      b,
      curv: k,
      born: Math.max(
        Math.max(nodes[a].birth, nodes[b].birth) + 0.14 + R() * 0.4,
        extra ? 6.6 + R() * 8 : 4.85 + R() * 4.1,
      ),
      o: extra ? 0.15 : nodes[a].chip || nodes[b].chip ? 0.16 : 0.26,
    });
  };
  for (const n of nodes) if (n.p >= 0) addEdge(n.i, n.p, 0);
  for (let i = 20; i < nodes.length; i++) {
    if (nodes[i].chip || R() > 0.3) continue;
    const j = Math.floor(R() * i);
    if (nodes[j].chip || j === nodes[i].p) continue;
    addEdge(i, j, 1);
  }

  const blinks = [];
  for (let i = 0; i < 54; i++) {
    const n = nodes[Math.floor(R() * nodes.length)];
    blinks.push({ n, t0: 19.85 + R() * 1.75, dur: 0.42 + R() * 0.5 });
  }

  const pulses = [];
  for (let i = 0; i < 30; i++) {
    const e = edges[Math.floor(R() * edges.length)];
    if (!e) continue;
    pulses.push({ e, t0: 9 + R() * 15, dur: 1.0 + R() * 0.9, gap: 2.6 + R() * 4.4 });
  }
  return { nodes, edges, pulses, blinks };
}

/**
 * The field is a pure function of its seed and independent of the host, so it
 * is built once for the page rather than per mount.
 */
let GRAPH: IGraph | null = null;
const graph = () => (GRAPH ||= buildGraph());

/* ------------------------------------------------------------------ *
 * the signature — where every item goes when the system signs its name
 * ------------------------------------------------------------------ */

interface ICell {
  si: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface IShape {
  x: number;
  y: number;
  w: number;
  h: number;
  rx: number;
}

interface IPer {
  cell: ICell | null;
  /** Launch time. */
  lt: number;
  /** Exit vector, for the surplus items that fly off instead. */
  ax?: number;
  ay?: number;
  dist?: number;
}

interface ILayout {
  shapes: IShape[];
  word: { x: number; y: number; w: number; h: number };
  per: IPer[];
}

function buildLayout(wordAr: number, wordCap: number): ILayout {
  const G = graph();
  const RN = mulberry(0x20bd1);
  const ms = 246 / 1024;
  const ww = 483;
  const wh = ww / wordAr;
  const cap = wh * wordCap;
  const word = { x: -93, y: -cap / 2, w: ww, h: wh };
  const mcx = -267;
  const mcy = word.y + cap / 2; // mark baseline-flush with the wordmark's cap band

  const X = (v: number) => (v - 512) * ms + mcx;
  const Y = (v: number) => (v - 280) * ms + mcy;
  const shapes: IShape[] = MARK.map((m) => ({
    x: X(m.x),
    y: Y(m.y),
    w: m.w * ms,
    h: m.h * ms,
    rx: m.pill ? (m.h * ms) / 2 : 0,
  }));

  const insideRR = (px: number, py: number, sh: IShape) => {
    const r = sh.rx;
    if (px < sh.x || px > sh.x + sh.w || py < sh.y || py > sh.y + sh.h) return false;
    if (!r) return true;
    const cx = Math.min(Math.max(px, sh.x + r), sh.x + sh.w - r);
    const cy = Math.min(Math.max(py, sh.y + r), sh.y + sh.h - r);
    return Math.hypot(px - cx, py - cy) <= r + 0.01;
  };

  // Tile each shape with item-sized cells.
  const cells: ICell[] = [];
  shapes.forEach((sh, si) => {
    const rows = Math.max(2, Math.round(sh.h / 12.5));
    const ch = sh.h / rows;
    const cols = Math.max(1, Math.round(sh.w / (ch * 1.62)));
    const cw = sh.w / cols;
    for (let r = 0; r < rows; r++) {
      for (let cc = 0; cc < cols; cc++) {
        const cell: ICell = {
          si,
          x: sh.x + cw * (cc + 0.5),
          y: sh.y + ch * (r + 0.5),
          w: Math.max(3, cw - Math.min(3.4, cw * 0.24)),
          h: Math.max(2.4, ch - Math.min(2.6, ch * 0.22)),
        };
        const hw = cell.w / 2;
        const hh = cell.h / 2;
        const ok =
          insideRR(cell.x - hw, cell.y - hh, sh) &&
          insideRR(cell.x + hw, cell.y - hh, sh) &&
          insideRR(cell.x - hw, cell.y + hh, sh) &&
          insideRR(cell.x + hw, cell.y + hh, sh);
        if (ok) cells.push(cell);
      }
    }
  });

  // Greedy nearest assignment from the settled rack positions, so nothing
  // crosses the frame to reach its cell.
  const taken = new Uint8Array(G.nodes.length);
  const cellOf: Record<number, ICell> = {};
  const ordered = cells.slice().sort((a, b) => a.si - b.si || a.y - b.y || a.x - b.x);
  for (const cell of ordered) {
    let best = -1;
    let bd = Infinity;
    for (let i = 0; i < G.nodes.length; i++) {
      if (taken[i]) continue;
      const n = G.nodes[i];
      const d = Math.hypot(n.ox - cell.x, n.oy - cell.y) - (n.chip ? 24 : 0);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    if (best < 0) break;
    taken[best] = 1;
    cellOf[best] = cell;
  }

  const per: IPer[] = G.nodes.map((n, i) => {
    const cell = cellOf[i] || null;
    const col = n.c / Math.max(1, COLS - 1);
    if (cell) {
      return {
        cell,
        lt: SIG.t0 + 0.38 + col * 0.26 + (Math.abs(n.oy) / 900) * 0.26 + RN() * 0.2,
      };
    }
    const a = Math.atan2(n.oy, n.ox || 0.001) + (RN() - 0.5) * 0.5;
    return {
      cell: null,
      ax: Math.cos(a),
      ay: Math.sin(a),
      dist: 720 + RN() * 520,
      lt: SIG.t0 + 0.34 + col * 0.24 + RN() * 0.5,
    };
  });
  return { shapes, word, per };
}

/* ------------------------------------------------------------------ *
 * the field, as a function of authored time
 * ------------------------------------------------------------------ */

function posOf(n: INode, T: number, L: ILayout): [number, number] {
  if (T > SIG.t0) {
    const p = L.per[n.i];
    if (T > p.lt) {
      if (p.cell) {
        const u = MOTION.draw(seg(T, p.lt, p.lt + SIG.move));
        return [n.ox + (p.cell.x - n.ox) * u, n.oy + (p.cell.y - n.oy) * u];
      }
      const u = MOTION.enter(seg(T, p.lt, p.lt + 1.05));
      return [n.ox + p.ax! * p.dist! * u, n.oy + p.ay! * p.dist! * u];
    }
    return [n.ox, n.oy];
  }
  if (T <= n.rt) return [n.sx, n.sy];
  if (T >= n.rt + MOVE) return [n.ox, n.oy];
  const u = MOTION.draw(seg(T, n.rt, n.rt + MOVE));
  return [n.sx + (n.ox - n.sx) * u, n.sy + (n.oy - n.sy) * u];
}

function sizeOf(n: INode, T: number, L: ILayout): [number, number] {
  if (T <= SIG.t0) return [n.w, n.h];
  const p = L.per[n.i];
  if (!p.cell || T <= p.lt) return [n.w, n.h];
  const u = MOTION.draw(seg(T, p.lt, p.lt + SIG.move));
  return [n.w + (p.cell.w - n.w) * u, n.h + (p.cell.h - n.h) * u];
}

function opOf(n: INode, T: number, L: ILayout): number {
  if (T <= SIG.t0) return 1;
  const p = L.per[n.i];
  if (p.cell) return 1;
  return 1 - MOTION.enter(clamp01(seg(T, p.lt + 0.1, p.lt + 0.95)));
}

/** Control point of an edge — curved while loose, straight once both ends settle. */
function edgeCurve(e: IEdge, T: number, L: ILayout) {
  const G = graph();
  const a = G.nodes[e.a];
  const b = G.nodes[e.b];
  const pa = posOf(a, T, L);
  const pb = posOf(b, T, L);
  const straight = Math.min(
    MOTION.draw(seg(T, a.rt, a.rt + MOVE)),
    MOTION.draw(seg(T, b.rt, b.rt + MOVE)),
  );
  const k = e.curv * (1 - straight);
  const dx = pb[0] - pa[0];
  const dy = pb[1] - pa[1];
  return {
    pa,
    pb,
    cp: [(pa[0] + pb[0]) / 2 - dy * k, (pa[1] + pb[1]) / 2 + dx * k] as [number, number],
  };
}

const qPoint = (
  pa: [number, number],
  cp: [number, number],
  pb: [number, number],
  u: number,
): [number, number] => {
  const m = 1 - u;
  return [
    m * m * pa[0] + 2 * m * u * cp[0] + u * u * pb[0],
    m * m * pa[1] + 2 * m * u * cp[1] + u * u * pb[1],
  ];
};

/* ------------------------------------------------------------------ *
 * the cursor — places the first items by hand, then is outrun
 * ------------------------------------------------------------------ */

const CUR_IDX = [0, 1, 2, 3, 4, 5, 6, 8, 10, 13, 17, 22, 29, 38];

/** The pointer's height on screen, in CSS pixels — an OS pointer's size. */
const CURSOR_PX = 20;
/** Height of the pointer artwork in its own units, for that conversion. */
const CURSOR_ART_H = 26;

/**
 * Which part of the composition the viewer can actually see. The field is meant
 * to run off the edges, but the cursor is not: an arrow clicking on something
 * off screen reads as a glitch rather than as a hand at work, so the items it
 * visits are chosen against this.
 */
interface IViewport {
  /** True when this point, at this time, lands on clear ground. */
  sees: (x: number, y: number, T: number) => boolean;
  /** Pull a point back onto clear ground. */
  clamp: (x: number, y: number, T: number) => [number, number];
}

interface ICursor {
  x: (T: number) => number;
  y: (T: number) => number;
  /** The items it actually gets to — a click only rings on one of these. */
  idx: number[];
}

/**
 * The cursor's path. It opens off to one side, clicks its way through the first
 * items and then drifts as creation outruns it. Items it could not be seen
 * clicking are dropped rather than followed off screen; the opening and the
 * drift carry no click, so those are pulled back into view instead.
 */
function buildCursor(view: IViewport | null): ICursor {
  const G = graph();
  const at = (i: number) => G.nodes[i].birth - 0.14 + (i > 6 ? (i - 6) * 0.045 : 0);
  let idx = CUR_IDX;
  if (view) {
    const kept = CUR_IDX.filter((i) => view.sees(G.nodes[i].sx, G.nodes[i].sy, at(i)));
    // Too few left to read as placement — better the artwork's own run than a
    // cursor that clicks twice and stops.
    if (kept.length >= 5) idx = kept;
  }

  const curT = [-1];
  const open: [number, number] = view ? view.clamp(-330, 340, 0) : [-330, 340];
  const curX = [open[0]];
  const curY = [open[1]];
  idx.forEach((i) => {
    const n = G.nodes[i];
    curT.push(at(i));
    curX.push(n.sx);
    curY.push(n.sy);
  });
  const tail = G.nodes[44];
  const drift: [number, number] = view ? view.clamp(tail.sx, tail.sy, 10.6) : [tail.sx, tail.sy];
  curT.push(10.6);
  curX.push(drift[0]);
  curY.push(drift[1]);
  return { x: pw(curT, curX), y: pw(curT, curY), idx };
}

/* ------------------------------------------------------------------ *
 * the camera
 * ------------------------------------------------------------------ */

const CAM = {
  z: pw(
    [0, INT[1], INT[2], INT[3], INT[4], SIG.t0, SIG.t0 + 2.5, TOTAL - 0.12, TOTAL],
    [
      Math.log(2.15),
      Math.log(1.42),
      Math.log(0.94),
      Math.log(0.8),
      Math.log(0.815),
      Math.log(0.83),
      Math.log(1.0),
      Math.log(1.0),
      Math.log(2.15),
    ],
  ),
  x: pw([0, INT[1], INT[2], INT[3], TOTAL], [-120, -40, 10, 0, 0]),
  y: pw([0, INT[1], INT[2], INT[3], TOTAL], [230, 90, -20, 0, 0]),
};

/** Where the camera is at an authored time, sway included. */
function camAt(T: number) {
  const damp = 1 - MOTION.enter(seg(T, SIG.t0, SIG.t0 + 1.3));
  const ph = (T / TOTAL) * Math.PI * 2;
  return {
    z: Math.exp(CAM.z(T)),
    x: CAM.x(T) + Math.sin(ph) * 22 * damp,
    y: CAM.y(T) + Math.cos(ph * 2) * 18 * damp,
  };
}

/* ------------------------------------------------------------------ *
 * reading the lockup
 * ------------------------------------------------------------------ */

interface IRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function raster(img: HTMLImageElement, targetH: number): HTMLCanvasElement {
  const ar = (img.naturalWidth || 1) / (img.naturalHeight || 1);
  const c = document.createElement('canvas');
  c.height = targetH;
  c.width = Math.max(1, Math.round(targetH * ar));
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  return c;
}

/** Repaint a raster in one flat colour, keeping its alpha. */
function tint(src: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = src.width;
  c.height = src.height;
  const g = c.getContext('2d')!;
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = color;
  g.fillRect(0, 0, c.width, c.height);
  return c;
}

const INK = 24; // alpha above this counts as artwork

/** Tight bounding box of the artwork between two columns. */
function bbox(
  d: Uint8ClampedArray,
  w: number,
  h: number,
  x0: number,
  x1: number,
): IRect | null {
  let minX = x1;
  let maxX = x0;
  let minY = h;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = x0; x <= x1; x++) {
      if (d[(y * w + x) * 4 + 3] <= INK) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxY < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/**
 * Split the lockup into its mark and its letters on the widest column of clear
 * space. Inter-letter gaps are far narrower than the gap the brand leaves
 * between the two, so the widest run is the one we want; anything short of a
 * meaningful gap means we were handed a mark on its own.
 */
function splitLockup(src: HTMLCanvasElement): IRect | null {
  const w = src.width;
  const h = src.height;
  const d = src.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, w, h).data;

  const inked = new Uint8Array(w);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      if (d[(y * w + x) * 4 + 3] > INK) {
        inked[x] = 1;
        break;
      }
    }
  }
  const first = inked.indexOf(1);
  const last = inked.lastIndexOf(1);
  if (first < 0) return null;

  let bestStart = -1;
  let bestLen = 0;
  let run = 0;
  for (let x = first; x <= last; x++) {
    if (inked[x]) {
      run = 0;
      continue;
    }
    run++;
    if (run > bestLen) {
      bestLen = run;
      bestStart = x - run + 1;
    }
  }

  const wide = bestLen >= h * 0.12;
  const cut = bestStart + Math.floor(bestLen / 2);
  // The mark leads the lockup, so a gap in the right-hand third is letter
  // spacing, not the split we are after.
  if (!wide || cut > first + (last - first) * 0.6) return null;
  return bbox(d, w, h, cut, last);
}

/**
 * The letters' baseline, as a fraction of their box height. Every row above it
 * carries a letter body; below it there is only the descender, which inks a
 * small fraction of the width — so the last row above a quarter coverage is the
 * baseline.
 */
function capOf(src: HTMLCanvasElement, r: IRect): number {
  const g = src.getContext('2d', { willReadFrequently: true })!;
  const d = g.getImageData(r.x, r.y, r.w, r.h).data;
  const rows = new Array<number>(r.h).fill(0);
  let max = 0;
  for (let y = 0; y < r.h; y++) {
    let n = 0;
    for (let x = 0; x < r.w; x++) if (d[(y * r.w + x) * 4 + 3] > INK) n++;
    rows[y] = n;
    if (n > max) max = n;
  }
  if (!max) return WORD_CAP;
  let base = -1;
  for (let y = r.h - 1; y >= 0; y--) {
    if (rows[y] >= max * 0.25) {
      base = y;
      break;
    }
  }
  if (base < 0) return WORD_CAP;
  return (base + 1) / r.h;
}

/* ------------------------------------------------------------------ *
 * mount
 * ------------------------------------------------------------------ */

export function mountBrandIntro(host: HTMLElement, opts: IBrandIntroOptions): IBrandIntroHandle {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'display:block;width:100%;height:100%';
  canvas.setAttribute('aria-hidden', 'true');
  host.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;

  const G = graph();
  let theme = opts.theme;
  let destroyed = false;
  let raf: number | null = null;
  let start: number | null = null;
  /** The time of the frame on screen, so a repaint holds its place. */
  let lastT = 0;
  /** Set while the lockup is still decoding, so a seek before then is not lost. */
  let pendingSeek: number | null = null;

  // Built once the lockup image has decoded.
  let art: HTMLCanvasElement | null = null; // untinted silhouette
  let wordRect: IRect | null = null;
  let wordPaint: HTMLCanvasElement | null = null;
  let layout: ILayout = buildLayout(WORD_AR, WORD_CAP);

  /** How much the authored timeline is stretched, 1 at the authored length. */
  const rate = Math.max(0.5, opts.duration || BRAND_INTRO_DURATION) / BRAND_INTRO_DURATION;
  /**
   * The last authored moment played. A loop runs the closing fade and zoom-out
   * that make its seam invisible; a single pass stops on the finished lockup
   * instead, so the logo is what stays on screen.
   */
  const endAt = opts.loop ? TOTAL : HOLD;
  /** Playback seconds for one pass. */
  const total = unwarp(endAt) * rate;

  const stage = Math.min(1, Math.max(0.1, opts.stage ?? 1));
  const focusX = Math.min(1, Math.max(0, opts.focusX ?? 0.5));
  /** The share of the canvas that is not faded out by the page over it. */
  const clearWidth = Math.min(1, Math.max(0.1, opts.clearWidth ?? 1));

  let dpr = 1;
  let cw = 0;
  let chh = 0;
  /** The stage's width — what the composition is laid out against. */
  let sw = 0;

  function measure() {
    const r = host.getBoundingClientRect();
    dpr = Math.min(2, window.devicePixelRatio || 1);
    cw = Math.max(1, Math.round(r.width));
    chh = Math.max(1, Math.round(r.height));
    sw = Math.max(1, Math.round(cw * stage));
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(chh * dpr);
  }

  function repaint() {
    if (!art) return;
    wordPaint = tint(art, theme.ink);
  }

  /** Author units per CSS pixel — the portrait frame covers the stage. */
  const fitOf = () => Math.max(sw / W, chh / H);

  /**
   * The clear ground, as the cursor sees it: stage-space points project to CSS
   * pixels through the camera, so the test has to be made at a given time.
   */
  function viewport(): IViewport {
    const fit = fitOf();
    const cx = cw * focusX;
    // Keep the whole pointer in, not just its hotspot.
    const pad = CURSOR_PX * 1.5;
    const right = cw * clearWidth - pad;
    const project = (x: number, y: number, T: number) => {
      const c = camAt(T);
      return { sx: cx + (x - c.x) * c.z * fit, sy: chh / 2 + (y - c.y) * c.z * fit, c };
    };
    return {
      sees: (x, y, T) => {
        const p = project(x, y, T);
        return p.sx >= pad && p.sx <= right && p.sy >= pad && p.sy <= chh - pad;
      },
      clamp: (x, y, T) => {
        const p = project(x, y, T);
        const k = p.c.z * fit;
        const nx = Math.min(Math.max(p.sx, pad), right);
        const ny = Math.min(Math.max(p.sy, pad), chh - pad);
        return [x + (nx - p.sx) / k, y + (ny - p.sy) / k];
      },
    };
  }

  let cursor: ICursor = buildCursor(null);

  function frame(tPlay: number) {
    lastT = tPlay;
    const T = Math.min(warp(tPlay / rate), endAt);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // The page's own background shows through; only the piece is painted.
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const fade = 1 - MOTION.draw(seg(T, TOTAL - 0.85, TOTAL - 0.18));
    if (fade <= 0.002) return;

    const fit = fitOf();
    const cam = camAt(T);
    const z = cam.z;
    const tx = cam.x;
    const ty = cam.y;

    const linkFade = 1 - MOTION.enter(seg(T, SIG.t0 + 0.2, SIG.t0 + 1.35));
    const solid = MOTION.enter(seg(T, SIG.solid, SIG.solid + 0.6));
    const wipe = MOTION.draw(seg(T, SIG.word, SIG.word + 0.8));
    const wordIn = MOTION.enter(seg(T, SIG.word, SIG.word + 0.4));

    // Frame space: the portrait composition, centred on the focus point.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.translate(cw * focusX - (W / 2) * fit, chh / 2 - (H / 2) * fit);
    ctx.scale(fit, fit);
    ctx.globalAlpha = fade;

    // Registration marks sit on the frame, outside the camera.
    const marks =
      0.14 * Math.min(MOTION.enter(seg(T, 1.0, 3.0)), 1 - seg(T, TOTAL - 0.75, TOTAL - 0.3));
    if (marks > 0.002) {
      ctx.save();
      ctx.globalAlpha = fade * marks;
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1 / fit;
      ctx.beginPath();
      // The crossbar leads into the frame, so it mirrors on the right edge.
      const tick = (x: number, y: number, dir: number) => {
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + 30);
        ctx.moveTo(x - 14 * dir, y + 15);
        ctx.lineTo(x + 16 * dir, y + 15);
      };
      tick(52, 36, 1);
      tick(W - 52, 36, -1);
      tick(52, H - 66, 1);
      tick(W - 52, H - 66, -1);
      ctx.stroke();
      ctx.restore();
    }

    // Camera.
    ctx.save();
    ctx.translate(W / 2, H / 2);
    ctx.scale(z, z);
    ctx.translate(-tx, -ty);

    /** One CSS pixel, in the units we are drawing in. */
    const hair = 1 / (fit * z);

    // Links. Every edge's opacity is its resting value times the one fade, so
    // the edges collapse into a handful of paths — one stroke per resting
    // opacity instead of one per edge.
    if (linkFade > 0.004) {
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = hair;
      const byOpacity = new Map<number, Path2D>();
      for (const e of G.edges) {
        if (e.born > T) continue;
        const p = MOTION.draw(seg(T, e.born, e.born + DRAW));
        if (p <= 0.001) continue;
        if (e.o * linkFade <= 0.004) continue;
        const { pa, pb, cp } = edgeCurve(e, T, layout);
        let path = byOpacity.get(e.o);
        if (!path) byOpacity.set(e.o, (path = new Path2D()));
        path.moveTo(pa[0], pa[1]);
        if (p >= 0.999) {
          path.quadraticCurveTo(cp[0], cp[1], pb[0], pb[1]);
          continue;
        }
        // Mid-draw: walk the curve out to the fraction drawn so far.
        const STEPS = 10;
        for (let s = 1; s <= STEPS; s++) {
          const pt = qPoint(pa, cp, pb, (s / STEPS) * p);
          path.lineTo(pt[0], pt[1]);
        }
      }
      for (const [o, path] of byOpacity) {
        ctx.globalAlpha = fade * o * linkFade;
        ctx.stroke(path);
      }
    }

    // Items.
    ctx.fillStyle = theme.ink;
    ctx.strokeStyle = theme.ink;
    for (const n of G.nodes) {
      if (n.birth > T) continue;
      const u = seg(T, n.birth, n.birth + POP);
      const popped = u >= 1;
      const s = popped ? 1 : 0.26 + MOTION.pop(u) * 0.74;
      const inMark = layout.per[n.i].cell;
      const dim = inMark
        ? n.dim + (1 - n.dim) * MOTION.enter(seg(T, SIG.t0 + 0.4, SIG.solid))
        : n.dim;
      const al = dim * opOf(n, T, layout) * MOTION.enter(clamp01(u * 3));
      if (al <= 0.004) continue;
      const pos = posOf(n, T, layout);
      const sz = sizeOf(n, T, layout);
      const hw = (sz[0] / 2) * s;
      const hh = (sz[1] / 2) * s;
      ctx.globalAlpha = fade * al;
      ctx.fillRect(pos[0] - hw, pos[1] - hh, hw * 2, hh * 2);
      // A hairline frame flashes out as the item is born.
      const flash = popped ? 0 : 1 - MOTION.enter(clamp01(u * 2.2));
      if (flash > 0.02) {
        const ow = hw + 6 * s;
        const oh = hh + 6 * s;
        ctx.globalAlpha = fade * flash * 0.6;
        ctx.lineWidth = hair;
        ctx.strokeRect(pos[0] - ow, pos[1] - oh, ow * 2, oh * 2);
      }
    }

    // Blinks — the ordered system breathing while it rests.
    for (const b of G.blinks) {
      if (T < b.t0 || T > b.t0 + b.dur) continue;
      const u = seg(T, b.t0, b.t0 + b.dur);
      const al = Math.sin(u * Math.PI) * (1 - b.n.dim);
      if (al <= 0.004) continue;
      const p = posOf(b.n, T, layout);
      ctx.globalAlpha = fade * al;
      ctx.fillRect(p[0] - b.n.w / 2, p[1] - b.n.h / 2, b.n.w, b.n.h);
    }

    // Pulses running the links.
    for (const p of G.pulses) {
      if (T < p.t0 || T > SIG.t0 + 0.7) continue;
      const cyc = (T - p.t0) % (p.dur + p.gap);
      if (cyc > p.dur || p.e.born + DRAW > T) continue;
      const u = cyc / p.dur;
      const { pa, pb, cp } = edgeCurve(p.e, T, layout);
      const pt = qPoint(pa, cp, pb, u);
      ctx.globalAlpha = fade * Math.sin(u * Math.PI) * 0.9;
      ctx.beginPath();
      ctx.arc(pt[0], pt[1], 2.4 * hair, 0, Math.PI * 2);
      ctx.fill();
    }

    // The solid mark takes over from the mosaic.
    if (solid > 0.002) {
      ctx.globalAlpha = fade * solid;
      for (const sh of layout.shapes) {
        ctx.beginPath();
        if (sh.rx) {
          const r = Math.min(sh.rx, sh.w / 2, sh.h / 2);
          ctx.moveTo(sh.x + r, sh.y);
          ctx.arcTo(sh.x + sh.w, sh.y, sh.x + sh.w, sh.y + sh.h, r);
          ctx.arcTo(sh.x + sh.w, sh.y + sh.h, sh.x, sh.y + sh.h, r);
          ctx.arcTo(sh.x, sh.y + sh.h, sh.x, sh.y, r);
          ctx.arcTo(sh.x, sh.y, sh.x + sh.w, sh.y, r);
        } else {
          ctx.rect(sh.x, sh.y, sh.w, sh.h);
        }
        ctx.fill();
      }
    }

    // The wordmark wipes in beside it.
    if (wordIn > 0.002 && wordPaint && wordRect) {
      const wd = layout.word;
      ctx.save();
      ctx.beginPath();
      ctx.rect(wd.x - 4, wd.y - 12, (wd.w + 8) * wipe, wd.h + 24);
      ctx.clip();
      ctx.globalAlpha = fade * wordIn;
      ctx.drawImage(
        wordPaint,
        wordRect.x,
        wordRect.y,
        wordRect.w,
        wordRect.h,
        wd.x,
        wd.y,
        wd.w,
        wd.h,
      );
      ctx.restore();
    }

    // The cursor.
    const cAlpha = Math.min(
      MOTION.enter(seg(T, 0.15, 0.6)),
      1 - MOTION.enter(seg(T, 9.6, 10.7)),
    );
    if (cAlpha > 0.004) {
      let click = 0;
      for (const i of cursor.idx) {
        const b = G.nodes[i].birth;
        if (T > b - 0.07 && T < b + 0.26) {
          click = Math.max(click, 1 - seg(T, b - 0.07, b + 0.26));
        }
      }
      // A fixed size on screen, so the pointer reads as a pointer whatever the
      // camera is doing and however the composition is fitted.
      const k = CURSOR_PX / CURSOR_ART_H / (z * fit);
      ctx.save();
      ctx.globalAlpha = fade * cAlpha;
      ctx.translate(cursor.x(T), cursor.y(T));
      ctx.scale(k, k);
      if (click > 0.01) {
        ctx.strokeStyle = theme.ink;
        ctx.lineWidth = hair / k;
        ctx.globalAlpha = fade * cAlpha * click * 0.45;
        ctx.beginPath();
        ctx.arc(1, 1, 7 + click * 15, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = fade * cAlpha;
      }
      ctx.translate(-2, -2);
      const cs = 1 - click * 0.13;
      ctx.scale(cs, cs);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, 23);
      ctx.lineTo(5.8, 17.6);
      ctx.lineTo(9.4, 26);
      ctx.lineTo(13, 24.4);
      ctx.lineTo(9.5, 16.3);
      ctx.lineTo(17.2, 16.2);
      ctx.closePath();
      ctx.fillStyle = theme.ink;
      ctx.fill();
      ctx.restore();
    }

    ctx.restore();
    ctx.globalAlpha = 1;
  }

  function tick(ts: number) {
    if (destroyed) return;
    if (start == null) start = ts;
    const t = (ts - start) / 1000;
    if (t < total) {
      frame(t);
      raf = requestAnimationFrame(tick);
      return;
    }
    if (!opts.loop) {
      raf = null;
      frame(total);
      return;
    }
    // The last of the pass has already faded out, so a gap holds an empty
    // frame rather than the finished lockup.
    if (t < total + opts.gap) {
      frame(total);
      raf = requestAnimationFrame(tick);
      return;
    }
    start = ts;
    frame(0);
    raf = requestAnimationFrame(tick);
  }

  function play() {
    if (destroyed) return;
    if (raf) cancelAnimationFrame(raf);
    if (opts.reducedMotion) {
      raf = null;
      // Hold the settled lockup: the mark solid, the wordmark fully wiped in.
      frame(REST * rate);
      return;
    }
    start = null;
    raf = requestAnimationFrame(tick);
  }

  function seek(t: number) {
    if (destroyed) return;
    if (!art) {
      pendingSeek = t;
      return;
    }
    if (raf) cancelAnimationFrame(raf);
    raf = null;
    frame(Math.min(total, Math.max(0, t)));
  }

  /**
   * Resize: the composition is fitted per frame, so this only re-measures — but
   * which items the cursor can be seen reaching depends on the shape of the
   * clear ground, so its path is rebuilt.
   */
  function onResize() {
    measure();
    cursor = buildCursor(viewport());
    if (raf == null) frame(lastT);
  }

  measure();
  cursor = buildCursor(viewport());
  let ro: ResizeObserver | null = null;
  if (window.ResizeObserver) {
    ro = new ResizeObserver(onResize);
    ro.observe(host);
  } else {
    window.addEventListener('resize', onResize);
  }

  // Hold until the lockup has decoded, so the signature never lands empty.
  const img = new Image();
  img.decoding = 'async';
  img.addEventListener('load', () => {
    if (destroyed) return;
    art = raster(img, 512);
    try {
      wordRect = splitLockup(art);
      if (wordRect) {
        // Lay the signature out against the artwork's own proportions rather
        // than the measured defaults, so the letters are never stretched.
        layout = buildLayout(wordRect.w / wordRect.h, capOf(art, wordRect));
      }
    } catch {
      // Reading the raster back is the one thing here that a cross-origin
      // lockup can refuse. The measured defaults describe the same artwork, so
      // fall back to them and keep the letters on their own half of it.
      wordRect = {
        x: Math.round(art.width * WORD_SHARE.x),
        y: 0,
        w: Math.round(art.width * WORD_SHARE.w),
        h: art.height,
      };
    }
    repaint();
    if (pendingSeek != null) {
      const t = pendingSeek;
      pendingSeek = null;
      seek(t);
    } else {
      play();
    }
  });
  // Nothing legible came back; run the piece without its wordmark rather than
  // leaving the panel empty.
  img.addEventListener('error', () => {
    if (!destroyed && raf == null && pendingSeek == null) play();
  });
  img.src = opts.lockupSrc;

  return {
    duration: total,
    setTheme(next) {
      theme = next;
      repaint();
      if (raf == null) frame(lastT); // repaint the held frame in the new theme
    },
    replay: play,
    seek,
    destroy() {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', onResize);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    },
  };
}
